// ============================================================
// TILT — TABLE KIT
// ------------------------------------------------------------
// The shared vocabulary every table is written in. A table file
// imports from here and returns a plain object. Nothing else.
//
// ── THE TABLE CONTRACT ───────────────────────────────────────
// A table is a DATA object with a few pure functions on it. It
// NEVER imports React. It NEVER touches Firebase. It NEVER
// reaches into the engine's internals. It declares:
//
//   id, name, kind          "score" | "trial" | "duel"
//   W, H                    playfield size in table units
//   gravity, damping        physics feel
//   balls                   how many you get
//   ballR                   ball radius
//   launch {x,y}            plunger position
//   launchDir {x,y}         launch vector (unit)
//   launchPower             base launch speed
//   flippers[]              Flipper constructor args
//   build(world) → []       returns the collider list
//   fields[]                permanent gravity zones (optional)
//   objectives[]            trial goals (optional; score tables omit)
//   scoreTable {}           tag → points (optional; falls back)
//   chargeTable {}          tag → charge (optional; falls back)
//   onHit(engine, e)        per-hit hook — lamp state, mode logic
//   onSensor(engine, e)     per-sensor hook
//   onTick(engine, dt)      per-frame hook — timers, rotation
//   lamps(engine) → {}      lamp state for the renderer
//   decor[]                 non-colliding art shapes (pure render)
//
// ── WHY TABLES ARE DATA ──────────────────────────────────────
// Because a table that's data can be:
//   • authored by a level editor later (no code deploy)
//   • serialized into Firebase for user-made tables
//   • hashed for a deterministic seed
//   • fed to an NPC/solver for auto-balancing
//   • rendered by a completely different frontend
// The instant a table imports React, all of that dies. So it
// doesn't. Ever. THIS IS THE HARDEST LAW IN TILT.
// ============================================================

import { Seg, Circle, Arc, Bumper, SensorSeg, SensorCircle, GravityZone } from "../physics/World";

// Standard playfield. Every table uses this box so the renderer,
// the aim math, and the sabotage coordinates all speak one unit.
export const TW = 400;    // table width
export const TH = 700;    // table height

// ══════════════════════════════════════════════════════════════
//  THE RESERVED CORRIDOR  —  A LAW, NOT A SUGGESTION
// ══════════════════════════════════════════════════════════════
//
// The plunger lane is a PROTECTED CORRIDOR. Nothing but shell()
// may put a collider inside it. Ever.
//
//     x = 0                       LANE_X    TW
//     ├──────── PLAYABLE ──────────┤ lane  ├─┤
//     │  build whatever you want   │ 370-396 │
//     │  here                      │ HANDS   │
//     │                            │  OFF    │
//
// Build a table's geometry inside PLAYABLE_R (=LANE_X) and you
// are safe. Cross it and you will wedge the ball.
//
// ── WHY THIS IS A LAW AND NOT A COMMENT ─────────────────────
// Spite's outlane wall sat at x = TW-24 = 376. That's SIX UNITS
// from the divider, inside a 26-unit corridor, with a 7-unit
// ball. The ball launched into a wedge and alternated hits
// between two walls every single physics step, bled 790 u/s of
// speed down to zero in half a second, and never moved. Score:
// nil. Forever.
//
// Nothing about the table file LOOKED wrong. `TW - 24` is a
// perfectly reasonable-looking number for an outlane. The bug
// was invisible in the source and invisible in a screenshot,
// and it only surfaced because a headless sim launched the ball
// and asked "did it get anywhere." That's the whole reason the
// sim exists, and the reason these bounds are now exported
// constants that assertLaneClear() actually checks.
//
// If you need a wall near the right edge, it goes at PLAYABLE_R
// or left of it. There is no version of this where you win an
// argument with the corridor.

export const LANE_X     = TW - 30;   // 370 — the divider (lane's left wall)
export const LANE_TOP   = 190;       // the divider's top edge
export const DEFLECT_Y  = 96;        // the deflector's low (right) end
export const PLAYABLE_R = LANE_X;    // ← BUILD LEFT OF THIS. ALWAYS.

/** Dev guard. Call it from a table's build() and it will throw
 *  loudly the moment a collider strays into the lane, instead of
 *  silently producing a ball that goes nowhere. Cheap insurance
 *  against the single most expensive bug in this codebase. */
export const assertLaneClear = (colliders, tableName = "table") => {
  // PLAYABLE_R is INCLUSIVE — a wall sitting exactly on the boundary
  // is legal and is in fact the correct place to put the right gutter.
  // (Only geometry strictly INSIDE the corridor wedges the ball.) If
  // this predicate rejected its own boundary, the constant would be
  // lying about what it means, and every table author would end up
  // guessing at a magic offset. It doesn't, so they don't.
  const inLane = (x, y) =>
    x > PLAYABLE_R && x < TW && y > LANE_TOP - 4;
  for (const c of colliders) {
    if (!c || c.sensor) continue;
    if (c.id === "wall_R" || c.id === "lane_wall" ||
        c.id === "lane_gate" || c.id === "lane_deflect") continue;
    let bad = false;
    if (c.kind === "seg")    bad = inLane(c.a.x, c.a.y) || inLane(c.b.x, c.b.y);
    if (c.kind === "circle") bad = inLane(c.c.x, c.c.y);
    if (bad) {
      throw new Error(
        `[TILT] ${tableName}: collider "${c.id}" intrudes into the RESERVED ` +
        `plunger corridor (x > ${LANE_X}, y > ${LANE_TOP}). The ball will ` +
        `wedge on launch and the table will score zero. Move it left of ` +
        `PLAYABLE_R (${PLAYABLE_R}). See TableKit.js — THE RESERVED CORRIDOR.`
      );
    }
  }
  return colliders;
};

// ══════════════════════════════════════════════════════════════
//  THE WEDGE LAW  —  THE GENERAL CASE OF THE CORRIDOR LAW
// ══════════════════════════════════════════════════════════════
//
// A ball is 2r wide. It needs 2r of AIR to pass between two walls.
// Any gap that is bigger than zero but smaller than 2r is a place
// a ball can enter and never leave. It squeezes in, alternates
// contact between both faces every physics step, bleeds all its
// speed into two restitutions, and stops. Forever.
//
// assertLaneClear() guarded ONE corridor. That was always too
// narrow a law — it was a special case wearing a general name. The
// plunger lane was simply the first place we NOTICED the wedge.
//
// ── WHAT THE SWEEP FOUND ────────────────────────────────────────
// A headless grid-drop of 660 balls across each shipped table:
//
//     The Foundry    11.5% of the playfield was a trap
//     Lockstep       24.1% of the playfield was a trap
//
// And the wall-pair audit named every one of them:
//
//     3.0u   lane_wall × tgt_0     ← target bank vs the divider
//     3.0u   lane_wall × tgt_1
//     3.0u   lane_wall × tgt_2
//     3.0u   lane_wall × tgt_3
//     3.0u   gut_R_in  × sling_R   ← the sling kicks INTO a 3u slot
//
// A 3-unit gap. With a 14-unit ball. The ball ground against
// `tgt_0` and `lane_wall` for 833 physics steps and stopped dead
// at 0.9 u/s. Every one of these was INVISIBLE in the source —
// `TW - 40` is a perfectly reasonable-looking number for a target.
//
// So the law is now stated in its true, general form:
//
//     NO TWO WALLS MAY BE 0 < gap < 2r + MARGIN APART.
//
// Either open the gap so the ball passes cleanly, or CLOSE it so
// the ball never enters. There is no legal middle. A gap the ball
// half-fits into is not a tight shot — it is a broken table.

/** The air a ball needs to survive a gap. 2r, plus a margin for
 *  the fact that a ball arriving at an angle needs more room than
 *  a ball arriving square. */
export const WEDGE_MARGIN = 4;

/** Minimum distance between two segments, surface to surface. */
const segGap = (A, B) => {
  const closest = (p, a, b) => {
    const vx = b.x - a.x, vy = b.y - a.y;
    const L = vx * vx + vy * vy || 1;
    let t = ((p.x - a.x) * vx + (p.y - a.y) * vy) / L;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    return { x: a.x + vx * t, y: a.y + vy * t };
  };
  const d = (p, q) => Math.hypot(p.x - q.x, p.y - q.y);
  let m = Infinity;
  for (const p of [A.a, A.b]) { const q = closest(p, B.a, B.b); m = Math.min(m, d(p, q)); }
  for (const p of [B.a, B.b]) { const q = closest(p, A.a, A.b); m = Math.min(m, d(p, q)); }
  return m - (A.thickness ?? 5) / 2 - (B.thickness ?? 5) / 2;
};

/** Distance from a circle's SURFACE to a segment's SURFACE.
 *
 *  ⚠ THIS CASE WAS MISSING FROM THE FIRST VERSION OF THE LAW, and
 *    the sweep found the hole within minutes. `assertNoWedges` only
 *    compared seg × seg, so a POST — which is a circle — slipped
 *    through the check entirely. The redesigned Foundry passed the
 *    law and STILL had a trap:
 *
 *        grinding: post_1 × ramp_L_railB   (840 steps, 5.7 u/s)
 *
 *    A round post 7 units off a ramp rail. The ball rolled into the
 *    crack between them and died there, 43 times out of 660 drops.
 *
 *    The lesson generalizes: a wedge does not care what SHAPE its
 *    two faces are. It only cares that there are two of them and
 *    that the ball fits between. Any law that privileges one
 *    primitive over another is not a law, it's a filter. */
const circSegGap = (C, S) => {
  const closest = (p, a, b) => {
    const vx = b.x - a.x, vy = b.y - a.y;
    const L = vx * vx + vy * vy || 1;
    let t = ((p.x - a.x) * vx + (p.y - a.y) * vy) / L;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    return { x: a.x + vx * t, y: a.y + vy * t };
  };
  const q = closest(C.c, S.a, S.b);
  return Math.hypot(C.c.x - q.x, C.c.y - q.y) - C.r - (S.thickness ?? 5) / 2;
};

/** Circle surface to circle surface. Two posts, or two bumpers, can
 *  pinch a ball between them exactly as well as two walls can. */
const circGap = (A, B) =>
  Math.hypot(A.c.x - B.c.x, A.c.y - B.c.y) - A.r - B.r;

/** THE WEDGE CHECK. Call it from build(). It throws on any gap a
 *  ball can enter but not exit — between ANY two solid shapes,
 *  segment or circle, in any combination.
 *
 *  Pairs may be explicitly exempted via `wedgeOk: true` on a
 *  collider — but the ONLY legitimate use of that is a gap the
 *  ball is never able to reach (e.g. two walls back-to-back inside
 *  solid geometry). If you find yourself reaching for it to silence
 *  a gap the ball CAN reach, you have not fixed the bug; you have
 *  hidden it, and the sweep will find it again. */
// ══════════════════════════════════════════════════════════════
//  THE CHAIN EXEMPTION  —  A CURVE IS NOT A TRAP
// ══════════════════════════════════════════════════════════════
//
// The wedge law compares every pair of solid faces and throws when
// they are 0 < gap < 2r. That was correct for a table made of
// straight walls. The instant the Midway landed, it became WRONG —
// and it announced this by throwing 170 violations, of which the
// smallest and angriest were:
//
//     0.6u   spiral_i8   ×  spiral_i10
//     0.8u   spiral_i7   ×  spiral_i9
//       1u   spiral_i6   ×  spiral_i8
//
// Read the ids. `i8` and `i10` are TWO LINKS APART IN THE SAME
// RAIL. They are consecutive pieces of ONE CONTINUOUS CURVED WALL,
// and they are close to each other because THAT IS WHAT A CURVE IS.
// The law had discovered that the spiral is smooth, and reported it
// as 170 fatal bugs.
//
// ── THE DEEPER POINT ─────────────────────────────────────────
// The wedge law's premise is: "two faces this close form a slot the
// ball can enter and not leave." That premise silently assumes the
// two faces are the boundaries of DIFFERENT objects, with a gap
// BETWEEN them that the ball can get into.
//
// Two links of one chain have no gap between them. They are the
// SAME SURFACE, tessellated. The ball can no more get "between"
// spiral_i8 and spiral_i10 than it can get between two pixels.
//
// So the law needed to learn the difference between
//     A GAP  (space between two walls)  and
//     A JOINT (the seam inside one wall)
// and it could not, because it had no idea that a wall could be
// made of more than one segment.
//
// ── THE FIX: `chain` ─────────────────────────────────────────
// Any collider may declare `chain: "<name>"`. Two colliders in the
// SAME chain are never compared against each other — they are
// understood to be one surface. Different chains are compared
// normally, so a spiral is still checked against the spire, the
// turnstile, and everything else on the deck. Which is what caught
// `spiral_i17 × spire_railB` — a REAL wedge, in the same run,
// hiding among 170 false ones.
//
// That last part is why this matters and why the exemption is
// narrow. A law that cries wolf 170 times has not made the table
// safe; it has made the ONE real bug unfindable. An assertion with
// a bad false-positive rate is worse than no assertion, because it
// trains you to skim the output — and the sim exists precisely
// BECAUSE these bugs are invisible when you skim.
//
// `chain` is NOT `wedgeOk`. `wedgeOk` says "I know, allow it."
// `chain` says "these two are not two." One is a silencer; the
// other is a correction to the law's model of the world.
const sameChain = (A, B) =>
  A.chain !== undefined && A.chain === B.chain;

export const assertNoWedges = (colliders, tableName = "table", ballR = 7) => {
  const need = 2 * ballR + WEDGE_MARGIN;
  const solid = colliders.filter(
    (c) => c && !c.sensor && !c.wedgeOk && (c.kind === "seg" || c.kind === "circle")
  );
  const bad = [];

  const gapOf = (A, B) => {
    if (A.kind === "seg"    && B.kind === "seg")    return segGap(A, B);
    if (A.kind === "circle" && B.kind === "circle") return circGap(A, B);
    if (A.kind === "circle" && B.kind === "seg")    return circSegGap(A, B);
    if (A.kind === "seg"    && B.kind === "circle") return circSegGap(B, A);
    return Infinity;
  };

  for (let i = 0; i < solid.length; i++) {
    for (let j = i + 1; j < solid.length; j++) {
      // Two links of ONE wall are not two walls. See THE CHAIN
      // EXEMPTION above — without this, every curve on the table
      // reports as a wedge, and the one REAL wedge in the list
      // becomes impossible to find.
      if (sameChain(solid[i], solid[j])) continue;
      const g = gapOf(solid[i], solid[j]);
      // g <= 0 → the shapes TOUCH. That's fine and it's often correct:
      // a sealed corner is not a trap, it's a wall. Only a gap the
      // ball can PARTIALLY enter is fatal.
      if (g > 0.5 && g < need) {
        bad.push({ a: solid[i].id, b: solid[j].id, gap: +g.toFixed(1) });
      }
    }
  }
  if (bad.length) {
    const list = bad
      .sort((x, y) => x.gap - y.gap)
      .slice(0, 12)
      .map((b) => `    ${String(b.gap).padStart(5)}u   ${b.a}  ×  ${b.b}`)
      .join("\n");
    throw new Error(
      `[TILT] ${tableName}: ${bad.length} WEDGE GAP(S). A ball is ${2 * ballR}u ` +
      `wide and needs ${need}u of air to pass. These gaps are big enough for ` +
      `the ball to enter and too small for it to leave — it will grind between ` +
      `both faces and stop dead:\n${list}\n` +
      `  Either OPEN the gap to >= ${need}u, or CLOSE it to 0 (let the shapes ` +
      `touch). There is no legal middle. See TableKit.js — THE WEDGE LAW.`
    );
  }
  return colliders;
};

// ══════════════════════════════════════════════════════════════
//  THE LEDGE LAW  —  A SINGLE FLAT WALL IS ALSO A TRAP
// ══════════════════════════════════════════════════════════════
//
// The wedge law needs TWO faces to catch a bug. It compares pairs.
// So it is structurally blind to the simplest trap of all:
//
//     A FLAT, LEVEL, UPWARD-FACING WALL.
//
// A ball lands on it. Gravity pulls straight down. The wall pushes
// straight up. There is no sideways force in the entire system, and
// so the ball does not move. Not slowly — AT ALL. Forever.
//
// ── HOW THE SWEEP FOUND IT ──────────────────────────────────────
// Lockstep, after the wedge law was already passing everywhere
// else, still reported 20.6% of its playfield as a trap. The
// diagnosis was not a pair of walls. It was one:
//
//     overhang      × 1669 contacts   ball at rest, v = 0.9
//     gw_floor      × 1671 contacts   ball at rest, v = 0.9
//     pocket_floor  × 1669 contacts   ball at rest, v = 0.9
//     gw_2          × 1665 contacts   ball at rest, v = 1.1
//
// 1,669 consecutive physics contacts with a SINGLE collider. The
// ball wasn't grinding between two faces — it was PARKED on one.
// Lockstep had built four horizontal floors, and every one of them
// was a shelf a ball could die on.
//
// ── THE FIX IS THE PINBALL TRUTH ───────────────────────────────
// A real pinball table is a PLANE ON A TILT. Nothing on it is
// level, because the whole machine is pitched forward six degrees.
// That is not decoration — it is the reason a real ball always,
// eventually, comes back to you.
//
// TILT's `World` models gravity as straight down (+y), which is
// the correct 2D projection of that tilted plane. But it means a
// table author can draw a perfectly horizontal segment, and the
// engine will happily let a ball sit on it until the heat death of
// the universe.
//
// So: NOTHING A BALL CAN LAND ON MAY BE LEVEL. Every upward-facing
// surface must be pitched at least MIN_PITCH, so that gravity has
// a component ALONG it and the ball always rolls somewhere.
//
// It doesn't matter which way it slopes. It matters that it slopes.

/** Minimum pitch (radians) for any upward-facing wall. ~16°.
 *
 *  ⚠ THIS NUMBER WAS MEASURED, NOT CHOSEN.
 *
 *  The first version of this law used 9° — a guess, and a bad one.
 *  Lockstep's pocket floors were duly re-pitched to ~11°, the law
 *  passed clean... and the sweep still found 53 dead balls sitting
 *  on those very floors. The law was satisfied and the table was
 *  still broken, which is the worst possible outcome: a check that
 *  makes you feel safe while the bug is still there.
 *
 *  So it got measured. A ball dropped onto a pitched floor, at
 *  every angle, and asked one question — did it leave?
 *
 *       5°   ✗ stuck at v=8.9
 *       8°   ✗ stuck at v=14.1
 *      10°   ✗ stuck at v=17.6
 *      12°   ✓ rolled off
 *      15°   ✓ rolled off
 *      20°+  ✓ rolled off
 *
 *  The cliff is between 10° and 12°: that is where gravity's
 *  along-surface component finally beats the solver's contact
 *  damping and the ball actually moves. 16° sits comfortably past
 *  it with margin for a heavier table (gravity varies per table,
 *  and Spite is 430 to The Foundry's 400).
 *
 *  A threshold you guessed is not a law. It's a superstition with
 *  a constant name. */
export const MIN_PITCH = 0.28;   // 16° — measured. see pitch sweep above.

/** THE LEDGE CHECK. Throws on any near-horizontal wall a ball can
 *  come to rest on top of.
 *
 *  A wall may opt out with `ledgeOk: true` — legitimate for a
 *  surface the ball physically cannot land on (a CEILING, whose
 *  upward face is unreachable) or one that KICKS (a ball can't rest
 *  on a surface that throws it). Both of those are checked for
 *  automatically below, so `ledgeOk` should almost never be needed. */
export const assertNoLedges = (colliders, tableName = "table") => {
  const bad = [];
  for (const c of colliders) {
    if (!c || c.sensor || c.kind !== "seg" || c.ledgeOk) continue;
    // A kicker can't be a shelf — it refuses to hold the ball still.
    if (c.kick) continue;
    // A one-sided wall whose solid face points DOWN is a ceiling.
    // A ball can't land on its top; it can only hit it from below.
    if (c.oneSided && (c.face?.y ?? -1) > 0) continue;

    const dx = c.b.x - c.a.x;
    const dy = c.b.y - c.a.y;
    if (Math.abs(dx) < 1e-6) continue;              // vertical: fine
    const pitch = Math.abs(Math.atan2(dy, dx));     // 0 = perfectly level
    const p = Math.min(pitch, Math.PI - pitch);     // fold to [0, π/2]
    if (p < MIN_PITCH) {
      bad.push({ id: c.id, deg: +((p * 180) / Math.PI).toFixed(1) });
    }
  }
  if (bad.length) {
    const list = bad
      .map((b) => `    ${String(b.deg).padStart(4)}°   ${b.id}`)
      .join("\n");
    throw new Error(
      `[TILT] ${tableName}: ${bad.length} LEVEL LEDGE(S). A ball that lands on ` +
      `a flat, upward-facing wall has NO sideways force acting on it and will ` +
      `sit there forever — the sim caught balls parked on exactly these, at ` +
      `0.9 u/s, for 1,600+ consecutive physics contacts:\n${list}\n` +
      `  A real pinball table is a plane on a tilt: NOTHING on it is level. ` +
      `Pitch each of these to at least ` +
      `${((MIN_PITCH * 180) / Math.PI).toFixed(0)}° (either direction — it ` +
      `only matters THAT it slopes), or give it a \`kick\` so it can't hold a ` +
      `ball still. See TableKit.js — THE LEDGE LAW.`
    );
  }
  return colliders;
};

/** Run every structural check a table must pass. One call, so a
 *  table author cannot forget half of them.
 *
 *  Three laws, three different blindnesses:
 *    • assertLaneClear — one protected corridor
 *    • assertNoWedges  — any PAIR of faces too close together
 *    • assertNoLedges  — any SINGLE face too flat to roll off
 *  Each was written the day after a bug the previous two couldn't
 *  see. There will be a fourth. Add it here when the sim finds it. */
export const assertTableSane = (colliders, tableName = "table", ballR = 7) => {
  assertLaneClear(colliders, tableName);
  assertNoWedges(colliders, tableName, ballR);
  assertNoLedges(colliders, tableName);
  return colliders;
};

// ── Wall helpers ──────────────────────────────────────────────

/** The outer shell every table needs: side walls + roof + a
 *  WORKING plunger lane. The BOTTOM is intentionally OPEN —
 *  that's where the drain lives.
 *
 *  ── THE PLUNGER LANE IS NOT A CHUTE ─────────────────────────
 *  If the lane is just two parallel walls, the ball shoots up,
 *  runs out of momentum, and falls STRAIGHT BACK DOWN THE SAME
 *  LANE into the drain. It never enters the playfield. The game
 *  is unplayable and the cause is invisible in a screenshot.
 *
 *  A working lane has three parts, in this order:
 *    1. the DIVIDER   — separates lane from playfield, ends at
 *                       LANE_TOP
 *    2. the DEFLECTOR — a diagonal ABOVE the divider that throws
 *                       the rising ball LEFT into open air
 *    3. the ONE-WAY   — a `oneSided` gate at the lane mouth so a
 *                       ball in play can't drop back down the
 *                       lane for a free ride to the plunger
 *
 *  All three, in that vertical order, or the table doesn't work. */
export const shell = (o = {}) => {
  const w = o.w ?? TW, h = o.h ?? TH;
  const t = o.thickness ?? 8;
  const laneX   = LANE_X;      // NOT overridable. The corridor is a law.
  const laneTop = LANE_TOP;
  const roofY   = DEFLECT_Y;

  // ── THE ORDERING RULE (get this wrong and nothing works) ────
  //
  //      y=48  ┌── deflector HIGH end (left)
  //            │  ╲
  //      y=96  │    ╲── deflector LOW end (right, at the wall)
  //            │        ← OPEN AIR. The ball is thrown through here.
  //     y=190  ├────  divider TOP  (laneTop)
  //            │
  //            │  the lane
  //            │
  //
  //   roofY (96) MUST be well ABOVE laneTop (190).
  //
  // The ball rides the lane, clears the divider's top edge, keeps
  // climbing through open air, and only THEN meets the deflector —
  // which throws it left across a table with nothing in the way.
  //
  // The original bug had the deflector BELOW the divider's top. The
  // deflector fired perfectly and hurled the ball straight into the
  // divider, 20 units away. The ball rattled in a sealed box and
  // scored zero, forever. The deflector was never the problem; the
  // ORDER was. Deflect into open air, or don't deflect at all.

  return [
    Seg(t/2, 0, t/2, h, { tag: "wall", thickness: t, restitution: 0.5, id: "wall_L" }),
    Seg(w - t/2, 0, w - t/2, h, { tag: "wall", thickness: t, restitution: 0.5, id: "wall_R" }),
    // The roof. `ledgeOk` because it IS a ceiling: the ball lives
    // BELOW it and can only ever strike its underside. Its flat top
    // faces out of the machine, where no ball can ever land. This is
    // the one legitimate use of the exemption — a surface the ball
    // physically cannot rest on is not a ledge, whatever its angle.
    Seg(0, t/2, w, t/2, {
      tag: "wall", thickness: t, restitution: 0.5,
      ledgeOk: true, id: "wall_T",
    }),

    // 1. THE DIVIDER — ends at laneTop, BELOW the deflector.
    Seg(laneX, h - 10, laneX, laneTop, {
      tag: "wall", thickness: 5, restitution: 0.35, friction: 0.02,
      id: "lane_wall",
    }),

    // 2. THE DEFLECTOR — sits ABOVE the divider, in open air.
    //    Runs from the right wall (low) up-and-left (high), so a
    //    ball rising along the wall is turned hard left.
    //
    //    It KICKS. A passive bounce leaves the exit velocity at the
    //    mercy of whatever the table's gravity and restitution
    //    happen to be — and Spite (gravity 430, heavier than the
    //    others) proved that: its ball got the same gentle nudge,
    //    fell out of the arch early, and dropped straight down its
    //    own deliberately-wide right gutter without ever passing a
    //    flipper. Same shell, same deflector, dead ball.
    //
    //    A KICK makes the throw DECISIVE and therefore portable: the
    //    ball leaves this ramp with real horizontal speed regardless
    //    of how heavy the table is. The launch has to work on every
    //    table the kit can build, not just the ones tuned for it.
    Seg(w - 8, roofY, laneX - 92, roofY - 48, {
      tag: "wall", thickness: 6, restitution: 0.5, friction: 0.01,
      kick: 150, id: "lane_deflect",
    }),

    // 3. THE ONE-WAY GATE — spans the lane mouth. A ball in play
    //    can't drop back down the lane for a free ride to the
    //    plunger. `face` points UP-LEFT (into the playfield): solid
    //    from above, transparent from inside the lane, so the launch
    //    passes straight through it.
    //
    //    ⚠ IT IS SLOPED, AND THAT MATTERS.
    //
    //    A FLAT gate is a SHELF. The real-launch sim found a ball
    //    coming to rest on top of it at (374, 180) and grinding
    //    there for 357 straight steps at 0.8 u/s — one launch in
    //    sixty, dead on a ledge inside the plunger corridor, where
    //    no flipper can ever reach it and no nudge is worth enough
    //    tilt to shake it loose.
    //
    //    A one-in-sixty ball loss to a shelf you cannot see is worse
    //    than an obvious bug, because the player will blame
    //    themselves for it.
    //
    //    So the gate now drops 6 units to the right. It is still a
    //    perfectly good one-way — the `face` normal does the actual
    //    work — but it is no longer FLAT, so a ball that lands on it
    //    rolls off into the lane instead of parking there forever.
    //    Nothing may be level in a place a ball can rest.
    //    A 6-unit drop was NOT ENOUGH — 4 launches in 200 still
    //    parked on it. A shelf with a slight tilt is still a shelf.
    //    The drop is now 16 units across a 26-unit span: a ~31°
    //    ramp, steep enough that gravity beats the ball's residual
    //    restitution and rolls it off every time. Measured, not
    //    guessed: at 6u it stuck 4/200; at 16u it sticks 0/200.
    //    ⚠ IT IS A KICKER, NOT A RAMP. THIS TOOK FOUR TRIES.
    //
    //    The lane mouth is 22 units wide (370 → 392). The ball is
    //    14. There is simply not enough room in there for a ball to
    //    sit on a slope and roll gently off it — whichever way the
    //    gate tilts, its low end forms a NOTCH with one of the two
    //    walls that bracket it, and the ball rolls obediently down
    //    the slope and into that notch. Measured:
    //
    //      FLAT             1/60  parked on the shelf
    //      SLOPE DOWN-RIGHT 4/200 wedged in `lane_gate × wall_R`
    //                             at (385,189) — the SAME pixel,
    //                             every time
    //      SLOPE DOWN-LEFT  17/200 worse. new notch, new corner.
    //
    //    A slope doesn't remove a trap, it MOVES the trap downhill.
    //    In a 22-unit box there is nowhere downhill to move it TO.
    //
    //    So stop trying to make the ball roll off, and make it
    //    IMPOSSIBLE FOR THE BALL TO STAY. The gate gets a `kick`.
    //    Touch it and you are thrown — hard, up and left, into the
    //    playfield. A ball cannot rest on a surface that refuses to
    //    hold still, and the gate's actual JOB (one-way: solid from
    //    above, transparent from below) is done entirely by the
    //    `face` normal and is completely unaffected by the kick.
    //
    //    It is flat again, because flatness was never the problem.
    //    The problem was believing that a passive surface in a gap
    //    barely wider than the ball could ever be safe. It can't.
    //    Nothing passive goes in a box that tight.
    Seg(laneX, laneTop, w - t, laneTop, {
      tag: "wall", thickness: 5, restitution: 0.3,
      oneSided: true, face: { x: 0, y: -1 },
      kick: 210, id: "lane_gate",
    }),
  ];
};

/** The top arch — the curved ceiling. The ball, having been thrown
 *  LEFT by the lane deflector, rides the inside of this and curls
 *  down the left side into the playfield.
 *
 *  Spans from the left wall (π) around the top and stops short of
 *  the lane (≈1.75π), leaving the lane's mouth open. Overshoot this
 *  and you re-seal the lane; undershoot and the ball escapes over
 *  the top. */
export const arch = (o = {}) => {
  const w = o.w ?? TW;
  const cy = o.cy ?? 190;
  const r  = o.r ?? (w / 2 - 26);
  // Spans the LEFT wall (π) up over the top and stops at ~1.62π —
  // short of the lane, so the deflected ball flies in UNDER it and
  // then rides its inside face down the left. Sweep too far right and
  // you block the deflector's flight path; too far left and the ball
  // sails over the top of everything.
  return [
    Arc(w / 2, cy, r, Math.PI * 1.00, Math.PI * 1.62, {
      tag: "wall", inner: true, thickness: 8, restitution: 0.45,
      friction: 0.02, id: "arch",
    }),
  ];
};

/** The drain — a sensor across the bottom, plus the two outlane
 *  gutters that feed it. The ONLY tag the engine treats as fatal. */
export const drain = (o = {}) => {
  const w = o.w ?? TW, h = o.h ?? TH;
  return [
    SensorSeg(0, h - 4, w, h - 4, {
      tag: "drain", thickness: 16, id: "drain_main",
    }),
  ];
};

/** Inlane/outlane guides — the classic "V" that funnels a ball
 *  either into your flipper (inlane, survivable) or straight down
 *  the gutter (outlane, fatal).
 *
 *  ── WHY THIS IS NOW ONE FUNCTION WITH slings() ───────────────
 *  It isn't — but it MUST agree with it. The old guides() and the
 *  old slings() were written independently, and they collided:
 *
 *      3.0u   gut_R_in  ×  sling_R
 *
 *  A 3-unit gap. The ball is 14. The right slingshot's lower end
 *  sat three units from the inlane guide, so the sling would fire
 *  the ball INTO a slot narrower than the ball, where it ground
 *  itself to a halt. Every table built on this kit inherited it.
 *
 *  The fix is not to nudge one number. It's to derive BOTH shapes
 *  from the SAME anchor points, so they cannot drift apart again.
 *  That's what LANE_GEO does. One source of truth; the sling and
 *  the guide read the same numbers, so a gap between them is now
 *  arithmetically impossible rather than merely unlikely. */

/** The shared anchor geometry for the lower third. guides() and
 *  slings() BOTH derive from this, so they can never disagree. */
export const LANE_GEO = (o = {}) => {
  const w  = o.w ?? TW;
  const fy = o.flipperY ?? (TH - 110);
  const r  = o.ballR ?? 7;
  const need = 2 * r + WEDGE_MARGIN;           // 18u — the ball's air requirement

  // ⚠ The RIGHT gutter must sit at or LEFT OF PLAYABLE_R (370). The
  //   first draft of this used `w - 22` = 378 — six units inside the
  //   plunger corridor — and assertLaneClear() threw on the very
  //   first build. Which is the entire point of having the law: it
  //   caught the author of the law, in the act, on his first try.
  const outR = Math.min(w - 34, PLAYABLE_R);   // 366 — clear of the lane
  const outL = 10;   // flush against wall_L's inner face — they TOUCH. gap=0. legal.

  // ⚠ THE OUTLANE IS ITSELF A GAP, AND IT MUST OBEY THE WEDGE LAW.
  //   This is the subtlety the old kit missed. The outlane's whole
  //   JOB is to swallow a ball — but a ball it swallows must reach
  //   the DRAIN, not jam halfway down. The old outlane was 15 units
  //   of air around a 14-unit ball: the ball fit, barely, and then
  //   ground against both walls the entire way down.
  //
  //   A fatal lane and a jammed lane look identical from the couch.
  //   They are not the same thing. One is pinball; the other is a
  //   bug. So the outlane is now `need` wide (18u) — comfortably
  //   more than the ball, so a ball that enters it FALLS, cleanly,
  //   all the way out. Losing a ball must always feel like losing,
  //   never like breaking.
  //   Centre-to-centre, NOT surface-to-surface: both walls are 5u
  //   thick, so 2.5u of each is eaten from the gap. The chute must
  //   be `need + 5 + slack` on centres to give `need` of actual AIR.
  //   Getting this wrong by one unit is how you ship a 17u outlane
  //   that a ball jams in — the law caught exactly that, twice.
  const lane = need + 5 + 6;                   // 29u on centres → 24u of air

  return {
    w, fy, need,
    outX_L: outL,
    outX_R: outR,
    // The inner guide — the inlane's outer edge.
    inX_L:  outL + lane,
    inX_R:  outR - lane,
    top:    fy - 132,
    bot:    fy + 12,
    // The slingshot's LOWER tip. It must land a FULL BALL-WIDTH clear
    // of the inlane guide, or the sling fires the ball into a slot it
    // can't get out of — which is precisely what the old kit did
    // (3.0u gut_R_in × sling_R, found by the sweep).
    slingLoX_L: outL + lane + need + 22,
    slingLoX_R: outR - lane - need - 22,
    slingLoY:   fy - 26,
    slingHiY:   fy - 90,
    // The sling's HIGH end. Also offset clear of the guide.
    slingHiX_L: outL + lane + need + 8,
    slingHiX_R: outR - lane - need - 8,
  };
};

export const guides = (o = {}) => {
  const G = LANE_GEO(o);
  return [
    // LEFT gutter (outer) — vertical, the drain road.
    //
    // ⚠ It must clear the SHELL WALL too. wall_L sits at x=4 with a
    //   thickness of 8, so its inner face is at x=8. A gutter at 22
    //   left 11.5u of air between them — a ball fits in, and jams.
    //   The sweep found it. The gutter's outer wall is now flush
    //   against the shell: they TOUCH, so there is no gap at all.
    //   "Close it or open it." This one we CLOSED.
    Seg(G.outX_L, G.top, G.outX_L, G.bot, {
      tag: "wall", thickness: 5, restitution: 0.4, id: "gut_L_out",
    }),
    // LEFT inlane guide (inner). Parallel to the gutter, NOT angled
    // into it — an angled guide narrows the outlane to a wedge at its
    // bottom end, which is exactly what the old kit did.
    Seg(G.inX_L, G.top + 8, G.inX_L, G.bot - 8, {
      tag: "wall", thickness: 5, restitution: 0.4, id: "gut_L_in",
    }),
    // RIGHT: mirrored.
    Seg(G.outX_R, G.top, G.outX_R, G.bot, {
      tag: "wall", thickness: 5, restitution: 0.4, id: "gut_R_out",
    }),
    Seg(G.inX_R, G.top + 8, G.inX_R, G.bot - 8, {
      tag: "wall", thickness: 5, restitution: 0.4, id: "gut_R_in",
    }),
  ];
};

/** Slingshots — the two angled kickers above the flippers. They're
 *  what keeps a ball ALIVE in the lower third. Without them the
 *  bottom of a table is a dead funnel and every ball drains in 3s.
 *
 *  Derived from LANE_GEO so the sling's lower tip lands a full
 *  ball-width clear of the inlane guide. It kicks into AIR. */
export const slings = (o = {}) => {
  const G = LANE_GEO(o);

  // ── THE PADS — the sling→flipper gap, CLOSED ────────────────
  //
  // ⚠ THE GAP WAS REAL AND IT WAS 52 UNITS WIDE. The sling's lower
  //   tip lands at (79, fy−26) and the flipper's PIVOT sits at
  //   ~(124, fy). Between them: open air, straight to the drain.
  //   Measured by grid drop: a dead ball released at x=92 or x=108
  //   above the band fell STRAIGHT DOWN, past the flipper's root,
  //   where no flip can ever reach it. It read as a cheap drain
  //   because it was one.
  //
  //   The pad is a short kicked rail from the sling's tip to the
  //   flipper's root. It slopes ~25° (past MIN_PITCH — a ball rolls
  //   off it TOWARD the flipper, which makes it a feed), and it
  //   kicks a little (120, jittered) so a dead ball dropped square
  //   on it is bounced back into play instead of grinding. It ends
  //   6u PAST the pivot on purpose: a pad that stops short of the
  //   root re-opens the gap it exists to close, one ball-radius
  //   smaller.
  //
  //   Flipper geometry is derived with the kit's own defaults
  //   (gap 62, length 62 — flipperPair's). A table with custom
  //   flippers can pass `flipperGap` / `flipperLength` through to
  //   keep the pads exact; a few units of extra overlap with the
  //   root is safe either way — the root is solid, and per the
  //   gutter's own law, "close it or open it": this one we CLOSED.
  //
  //   Proof (headless, all three tables): builds pass every law;
  //   band drops at x=92/108 (L) and x=276 (R) are now deflected
  //   back into play instead of falling through; the 20-launch
  //   sweep shows no new parks attributable to the pads.
  const fGap = o.flipperGap ?? 62;
  const fLen = o.flipperLength ?? 62;
  const fxL = G.w / 2 - fGap / 2 - fLen * 0.72;
  const fxR = G.w / 2 + fGap / 2 + fLen * 0.72;

  return [
    Seg(G.slingHiX_L, G.slingHiY, G.slingLoX_L, G.slingLoY, {
      tag: "slingshot", thickness: 8, restitution: 0.35,
      kick: 235, kickJitter: 0.14, id: "sling_L",
    }),
    Seg(G.slingHiX_R, G.slingHiY, G.slingLoX_R, G.slingLoY, {
      tag: "slingshot", thickness: 8, restitution: 0.35,
      kick: 235, kickJitter: 0.14, id: "sling_R",
    }),
    Seg(G.slingLoX_L, G.slingLoY, fxL + 6, G.fy - 2, {
      tag: "slingshot", thickness: 8, restitution: 0.45,
      kick: 120, kickJitter: 0.14, id: "pad_L",
    }),
    Seg(G.slingLoX_R, G.slingLoY, fxR - 6, G.fy - 2, {
      tag: "slingshot", thickness: 8, restitution: 0.45,
      kick: 120, kickJitter: 0.14, id: "pad_R",
    }),
  ];
};

/** A standard flipper pair, correctly placed. */
export const flipperPair = (o = {}) => {
  const w = o.w ?? TW, h = o.h ?? TH;
  const y = o.y ?? h - 110;
  const gap = o.gap ?? 62;         // the drain gap between the tips
  const L = o.length ?? 62;
  const cx = w / 2;
  return [
    { id: "fl", side: "L", x: cx - gap / 2 - L * 0.72, y, length: L },
    { id: "fr", side: "R", x: cx + gap / 2 + L * 0.72, y, length: L },
  ];
};

/** A drop-target bank — N targets in a row that light and stay lit. */
export const targetBank = (x, y, n, o = {}) => {
  const out = [];
  const sp = o.spacing ?? 22;
  const w  = o.w ?? 14;
  const ang = o.angle ?? 0;
  for (let i = 0; i < n; i++) {
    const px = x + Math.cos(ang) * (i * sp);
    const py = y + Math.sin(ang) * (i * sp);
    out.push(Seg(px, py, px + Math.cos(ang + Math.PI/2) * 0, py + w, {
      tag: o.tag ?? "target", thickness: 9, restitution: 0.6,
      id: `${o.prefix ?? "tgt"}_${i}`,
    }));
  }
  return out;
};

/** A ramp — an entrance sensor + a pair of guide rails + a scoring
 *  sensor at the top. Making the ramp is easy; making it FEEL good
 *  is about the entrance angle. Aim the mouth at the flipper. */
export const ramp = (o) => {
  const { ax, ay, bx, by, id, width = 20 } = o;
  const dx = bx - ax, dy = by - ay;
  const l = Math.hypot(dx, dy) || 1;
  const nx = -dy / l * (width / 2), ny = dx / l * (width / 2);
  return [
    Seg(ax + nx, ay + ny, bx + nx, by + ny, {
      tag: "wall", thickness: 5, restitution: 0.35, id: `${id}_railA`,
    }),
    Seg(ax - nx, ay - ny, bx - nx, by - ny, {
      tag: "wall", thickness: 5, restitution: 0.35, id: `${id}_railB`,
    }),
    SensorSeg(bx + nx, by + ny, bx - nx, by - ny, {
      tag: o.tag ?? "ramp", thickness: 10, id: `${id}_score`,
    }),
  ];
};

/** A ghostable wall — Ghost passes through it, nothing else does.
 *  The lock that Ghost is the key to. Trial tables live on these. */
export const ghostWall = (ax, ay, bx, by, o = {}) =>
  Seg(ax, ay, bx, by, {
    // ⚠ SPREAD `o` FIRST. This helper used to hand-pick four fields
    //   and drop the rest — the same allowlist trap as Seg() itself.
    //   `ledgeOk: true` was passed in and silently vanished, and the
    //   ledge law kept throwing on a ceiling that had already been
    //   exempted. Twice in one file, the same bug: an object that
    //   quietly forgets the properties you gave it.
    //
    //   If a helper takes an options bag, it must PASS THE BAG ON.
    //   Curating it is how flags disappear.
    ...o,
    tag: o.tag ?? "ghostwall", thickness: o.thickness ?? 6,
    restitution: o.restitution ?? 0.5, ghostable: true, id: o.id,
  });

/** An objective node — a sensor disc that a trial table counts. */
/** An objective node — a sensor disc that a trial table counts.
 *
 *  `dwell` (seconds) makes it a HOLD lock instead of a touch lock:
 *  the ball must remain inside it continuously for `dwell` seconds.
 *
 *      node(66, 402, "node_a")                  ← plain rollover
 *      node(200, 90, "node_d", { dwell: 0.35 }) ← must be HELD
 *
 *  ⚠ SPREAD `o` FIRST. This helper used to hand-pick `r`, `tag` and
 *    `id` and silently drop the rest — the THIRD allowlist in this
 *    codebase to eat a flag I'd just set (after Seg and ghostWall).
 *    `dwell: 0.35` went in and vanished, and the lock stayed open.
 *
 *    If a helper takes an options bag, PASS THE BAG ON. Curating it
 *    is how properties disappear between the call and the object,
 *    with no error and nothing to grep for. */
export const node = (x, y, id, o = {}) =>
  SensorCircle(x, y, o.r ?? 11, { ...o, tag: o.tag ?? "node", id });

// ══════════════════════════════════════════════════════════════
//  THE MIDWAY  —  THE CARNIVAL VOCABULARY
// ══════════════════════════════════════════════════════════════
//
// Everything above this line builds a PINBALL TABLE: walls, a
// ramp, a bumper, a gutter. That vocabulary is complete, correct,
// and it is the reason the last redesign of Spite added two
// bumpers and nothing else. NOT because the designer lacked
// ambition — because THE KIT COULD NOT SAY THE WORD "LOOP."
//
// A table is data. A table author can only build what the kit can
// EXPRESS. If the only nouns are `Seg`, `Circle` and `ramp`, then
// every "redesign" is a rearrangement of segs, circles and ramps,
// forever, no matter how the brief is phrased. You cannot ask for
// a rollercoaster in a language that has no word for track.
//
// So this section is the missing half of the language. Every
// device below is built ENTIRELY out of the three primitives the
// solver already knows (seg, circle, arc) plus the flags it
// already reads (kick, oneSided, sensor, dwell, ghostable). NOT
// ONE LINE OF PHYSICS CHANGES. The solver stays exactly as
// deterministic as it was this morning.
//
// What changes is what a table can SAY.
//
// ── THE DEVICES ──────────────────────────────────────────────
//   loop()        a full 360° whip-around orbit
//   spiral()      a descending helix — the corkscrew
//   scoop()       a hole that CATCHES, holds, and spits back
//   dropHole()    a hole that catches and DROPS you elsewhere
//   whirlpool()   a funnel that swallows and re-serves
//   captive()     a ball-in-a-cage you punch to score
//   gate()        a one-way flap (a real one, that opens)
//   turnstile()   a rotating cross — onTick spins it
//   coaster()     a rail a ball RIDES, with a lift hill
//   cart()        a moving platform — a kinematic seg
//   chute()       a slide: enters at A, exits at B, fast
//   fork()        a wishbone — one mouth, two exits, RNG picks
//   wheel()       the carnival wheel. Land in a wedge, win a prize.
//
// ── THE LAW THIS SECTION LIVES UNDER ─────────────────────────
// Every device here is composed of colliders that go through the
// SAME assertTableSane(). A loop is not exempt from the wedge
// law. A scoop is not exempt from the ledge law. If a device
// builds a trap, the sim finds it and the build throws, exactly
// as it does for a hand-rolled wall.
//
// THE ONE EXCEPTION, STATED LOUDLY:
//   A device that CATCHES a ball on purpose (scoop, whirlpool,
//   dropHole, wheel) is, by definition, a trap. That is its JOB.
//   The difference between a scoop and a bug is not whether the
//   ball gets stuck — it's WHETHER SOMETHING GETS IT OUT.
//
//   So every catching device MUST ship with its own escape:
//   an `eject` (a kick), or a `dwell` sensor wired to a table
//   hook that teleports. A catcher without an ejector is not a
//   device, it is the wedge law wearing a costume. Every catcher
//   below carries its ejector with it, in the same function, so
//   you cannot author one without the other.
// ══════════════════════════════════════════════════════════════

const TAU = Math.PI * 2;

// ── LOOP — the full orbit ────────────────────────────────────
/** A 360° whip. The ball enters the mouth, is thrown around the
 *  inside of a circle, and comes out flying. The single most
 *  satisfying shape in pinball and the kit did not have it.
 *
 *  Built from TWO arcs (an inner wall and an outer wall) with a
 *  ball-safe channel between them, and a MOUTH — a gap in both
 *  arcs where the ball gets in and out.
 *
 *  ⚠ THE CHANNEL MUST OBEY THE WEDGE LAW.
 *    inner radius and outer radius differ by `width`. The arcs
 *    are `thickness` thick, so the actual AIR is
 *    `width - thickness`. If that's less than 2r+MARGIN the ball
 *    grinds around the loop and dies halfway. So `width` has a
 *    floor and the function ENFORCES it rather than trusting the
 *    caller — because "the ball doesn't fit in my loop" is
 *    invisible in the source and obvious in the sim.
 *
 *  `mouth` — the angle (radians) the entry/exit gap is centred on.
 *  `spin`  — a kick along the outer wall. Makes the orbit WHIP
 *            instead of decay. A loop the ball crawls around is
 *            a corridor; a loop it's FLUNG around is a loop. */
export const loop = (o) => {
  const {
    cx, cy, r = 62, id = "loop",
    mouth = Math.PI / 2,     // default: mouth at the bottom
    gap = 0.9,               // radians of opening
    spin = 90,               // outer-wall kick — the whip
    tag = "loop",
    ballR = 7,
  } = o;

  const th = 5;
  // The channel must fit the ball with room to spare. Not a
  // suggestion — a computed floor.
  const width = Math.max(o.width ?? 26, 2 * ballR + WEDGE_MARGIN + th + 2);
  const rIn  = r;
  const rOut = r + width;

  // The arcs run from one lip of the mouth all the way around to
  // the other lip.
  const a0 = mouth + gap / 2;
  const a1 = mouth - gap / 2 + TAU;

  return [
    // Inner wall — the ball rides its OUTSIDE face.
    //
    // ⚠ IT KICKS TOO, AND THAT IS NOT SYMMETRY FOR ITS OWN SAKE.
    //
    //   The first loop put `spin` on the OUTER wall only, which is
    //   where the intuition says it belongs: the outer wall is the
    //   one the ball is flung against, so that's the one that should
    //   fling back. Correct — for a ball going FAST.
    //
    //   A ball that enters SLOW never reaches the outer wall at all.
    //   It drops to the bottom of the ring, lands on the INNER wall's
    //   upper face, and stops. The inner wall of a loop, at the
    //   bottom, is a BOWL — and the sweep found 22 balls sitting in
    //   it out of 304. Seven percent of the playfield, dead, inside
    //   the ride the table is proudest of.
    //
    //   The loop is not a wall the ball bounces off. It is a
    //   CHANNEL, and a channel's entire job is to keep the ball
    //   MOVING THROUGH IT. Any face of that channel a slow ball can
    //   come to rest against is a trap, whichever side it's on. So
    //   both faces kick — softly on the inside (it is a banister, not
    //   a bumper) and hard on the outside (that's the whip).
    //
    //       IF A DEVICE'S PURPOSE IS TRANSIT, EVERY SURFACE IN IT
    //       MUST REFUSE TO HOLD STILL.
    Arc(cx, cy, rIn, a0, a1, {
      tag: "wall", inner: false, thickness: th,
      restitution: 0.42, friction: 0.01, kick: 34, id: `${id}_in`,
    }),
    // Outer wall — the ball rides its INSIDE face. This is the one
    // that whips: the kick points inward, along the ball's travel.
    Arc(cx, cy, rOut, a0, a1, {
      tag: "wall", inner: true, thickness: th,
      restitution: 0.5, friction: 0.005, kick: spin, id: `${id}_out`,
    }),
    // The scoring gate — sits at the far side of the loop (opposite
    // the mouth), so it only fires if the ball ACTUALLY went round.
    // A sensor at the mouth would fire on a ball that merely brushed
    // the entrance and fell out again, and "did the ball complete
    // the loop" is the entire question a loop asks.
    SensorSeg(
      cx + Math.cos(mouth + Math.PI) * (rIn + 2),
      cy + Math.sin(mouth + Math.PI) * (rIn + 2),
      cx + Math.cos(mouth + Math.PI) * (rOut - 2),
      cy + Math.sin(mouth + Math.PI) * (rOut - 2),
      { tag, thickness: 8, id: `${id}_round` }
    ),
  ];
};

// ── SPIRAL — the corkscrew ───────────────────────────────────
/** A descending helix. Two spiral rails, `turns` revolutions,
 *  shrinking radius. The ball goes in at the top edge and comes
 *  out the bottom of the coil, dizzy.
 *
 *  Approximated as a chain of short segments (the solver has no
 *  spiral primitive and doesn't need one — a spiral IS a chain of
 *  segments; that's what a spiral is in a renderer too).
 *
 *  ⚠ EVERY SEGMENT IS PITCHED BY CONSTRUCTION. A spiral has no
 *    level tangent except at exactly two points per turn, and the
 *    `steps` resolution keeps those from landing exactly flat —
 *    but the ledge law checks each seg regardless, so if you crank
 *    `steps` high enough to produce a level one, it throws. Which
 *    is correct: a spiral with a flat spot has a flat spot. */
export const spiral = (o) => {
  const {
    cx, cy, r0 = 90, r1 = 34, turns = 1.5, a0 = 0,
    id = "spiral", width = 24, steps = 30, tag = "spiral", ballR = 7,
  } = o;

  const w = Math.max(width, 2 * ballR + WEDGE_MARGIN + 5);

  // ══════════════════════════════════════════════════════════
  //  THE SPIRAL PITCH LAW — a spiral can wedge ITSELF
  // ══════════════════════════════════════════════════════════
  //
  // The first Midway build threw 178 wedge gaps and 170 of them
  // were the spiral against ITS OWN PREVIOUS TURN:
  //
  //     0.9u   spiral_i19  ×  spiral_i21
  //     1.3u   spiral_i18  ×  spiral_i20
  //     1.7u   spiral_i17  ×  spiral_i19
  //
  // Not the spiral against the table. The spiral against the
  // spiral, one revolution earlier. Look at the ids: i19 and i21
  // are two steps apart in the CHAIN but one full TURN apart in
  // SPACE — and they were less than a unit from each other.
  //
  // ── THE ARITHMETIC NOBODY DOES BY EYE ────────────────────
  // Between turn N and turn N+1 the radius drops by
  //
  //     Δr = |r1 - r0| / turns
  //
  // That gap has to hold: the OUTER rail of the inner turn, the
  // INNER rail of the outer turn, and A WHOLE BALL between them.
  // So the real requirement is
  //
  //     Δr  >  width + 2r + MARGIN
  //
  // My table asked for r0=54, r1=28, turns=1.15 → Δr = 22.6u,
  // against a requirement of 24 + 18 = 42u. It was off by nearly
  // 2×, and it looked completely reasonable in the source. Four
  // numbers, all plausible, product fatal.
  //
  // ── WHY THIS IS ENFORCED AND NOT DOCUMENTED ──────────────
  // Because a comment saying "mind your radial pitch" would be
  // read once, by me, today. The next person to build a spiral
  // will pick four numbers that look nice, ship a table that
  // grinds the ball to death on turn two, and have no idea why —
  // exactly as I just did.
  //
  // A law you have to REMEMBER is not a law. It's a hope. So the
  // device computes its own minimum turn count and clamps to it,
  // and tells you it did.
  const need = 2 * ballR + WEDGE_MARGIN;
  const dr = Math.abs(r1 - r0);
  const maxTurns = dr / (w + need);
  const T = Math.min(turns, Math.max(0.35, maxTurns));
  if (T < turns - 1e-6 && typeof console !== "undefined") {
    console.warn(
      `[TILT] spiral "${id}": ${turns} turns would put consecutive ` +
      `coils ${(dr / turns).toFixed(1)}u apart, but the channel is ${w}u ` +
      `and the ball needs ${need}u — the spiral would WEDGE AGAINST ` +
      `ITSELF. Clamped to ${T.toFixed(2)} turns. To get more turns, ` +
      `widen the radius span (r0-r1) or narrow the channel.`
    );
  }

  const out = [];
  const pt = (t, off) => {
    const a = a0 + t * T * TAU;          // ← T, the CLAMPED turn count
    const rr = r0 + (r1 - r0) * t + off;
    return { x: cx + Math.cos(a) * rr, y: cy + Math.sin(a) * rr };
  };

  for (let i = 0; i < steps; i++) {
    const t0 = i / steps, t1 = (i + 1) / steps;
    const iA = pt(t0, -w / 2), iB = pt(t1, -w / 2);
    const oA = pt(t0,  w / 2), oB = pt(t1,  w / 2);
    // Rails are KICKERS, lightly. A spiral is a long channel and a
    // long channel bleeds speed; without a nudge the ball stalls
    // two-thirds of the way down and parks. The kick is small — it
    // isn't a bumper, it's a BANISTER.
    // ⚠ `chain` — these rails are ONE WALL, tessellated. Two links
    //   of the same chain are never wedge-checked against each other
    //   (see THE CHAIN EXEMPTION). Without it, a spiral reports every
    //   one of its own joints as a fatal trap — 170 of them, on the
    //   first build — and the one REAL wedge hiding in that list
    //   becomes impossible to see.
    out.push(Seg(iA.x, iA.y, iB.x, iB.y, {
      tag: "wall", thickness: 5, restitution: 0.4, friction: 0.01,
      kick: 22, id: `${id}_i${i}`, chain: `${id}_inner`,
    }));
    out.push(Seg(oA.x, oA.y, oB.x, oB.y, {
      tag: "wall", thickness: 5, restitution: 0.4, friction: 0.01,
      kick: 22, id: `${id}_o${i}`, chain: `${id}_outer`,
    }));
  }
  // Exit sensor at the inner end.
  const e = pt(1, 0);
  out.push(SensorCircle(e.x, e.y, 10, { tag, id: `${id}_exit` }));
  return out;
};

// ── SCOOP — the hole that catches and SPITS ──────────────────
/** A saucer. The ball rolls in, is HELD (dwell), the table does
 *  something dramatic, and then the scoop KICKS IT BACK OUT.
 *
 *  This is the single most important device in the section,
 *  because it is the one that makes a MINIGAME possible. A
 *  minigame needs the ball to STOP so the player can look at
 *  something else for a second. Pinball's answer to that, for
 *  seventy years, has been the scoop.
 *
 *  ── THE ANATOMY (and why it isn't a wedge) ──────────────────
 *  A cup: three walls forming a pocket, all PITCHED (ledge law),
 *  wide enough inside that the ball sits in it without grinding
 *  (wedge law), with a `dwell` sensor at the bottom that tells the
 *  table "he's in." The table then calls `ejectFrom()` — which is
 *  just a velocity write, the same one `vent` already does — and
 *  the ball leaves at `ejectAngle`, hard.
 *
 *  The pocket has NO LID. A ball can bounce out on its own if it
 *  arrives too fast, and that's correct — a scoop you can rattle
 *  out of is a scoop with a skill component. */
export const scoop = (o) => {
  const {
    x, y, id = "scoop", tag = "scoop",
    r = 15,                   // the cup's inner radius
    dwell = 0.18,             // how long the ball must SETTLE
    face = -Math.PI / 2,      // which way the mouth opens (up, default)
    ballR = 7,
  } = o;

  // The cup must be comfortably wider than the ball, or the ball
  // jams in the mouth instead of settling in the bowl. Same law,
  // different costume.
  const R = Math.max(r, ballR + 7);
  const out = [];
  // The bowl: an arc that spans everything EXCEPT the mouth.
  out.push(Arc(x, y, R, face + 0.62, face - 0.62 + TAU, {
    tag: "wall", inner: true, thickness: 6,
    restitution: 0.05,       // DEAD. A scoop absorbs. That's the catch.
    friction: 0.5,           // and it GRIPS, so the ball stops rattling.
    id: `${id}_cup`,
  }));
  // The catcher. `dwell` is the whole trick: a ball that merely
  // grazes the mouth doesn't count. It has to SIT DOWN.
  out.push(SensorCircle(x, y, R - 4, {
    tag, dwell, id: `${id}_hold`,
  }));
  return out;
};

/** The other half of every scoop. Call it from onSensor(). A
 *  catcher without this is a bug with a nice name. */
export const ejectFrom = (ball, ang, power = 420) => {
  if (!ball || !ball.alive) return;
  ball.vel = { x: Math.cos(ang) * power, y: Math.sin(ang) * power };
};

/** Teleport a ball, alive, to a point — with a velocity. The
 *  mechanism behind chutes, drop-holes, wormholes, and Spite's
 *  original `vent`. Generalised so every device shares one verb. */
export const warpBall = (ball, x, y, vx = 0, vy = -180) => {
  if (!ball || !ball.alive) return;
  ball.p = { x, y };
  ball.vel = { x: vx, y: vy };
  // A warped ball is a FRESH ball as far as sensors go — otherwise
  // it arrives still latched into whatever it left, and that sensor
  // is dead for the rest of its life. (The engine already learned
  // this lesson on respawn; the same trap is here.)
  ball.inSensor = {};
};

// ── DROP HOLE — catches, then drops you SOMEWHERE ELSE ───────
/** A hole with an exit at the other end. Fall in here, come out
 *  there. Sensor-only: the ball simply passes over it and the
 *  table's onSensor warps it. That means it's INVISIBLE to the
 *  wedge and ledge laws, because it contains no solid geometry at
 *  all — a hole is an ABSENCE, and the only way to model an
 *  absence in a solid-body solver is a sensor plus a hook. */
export const dropHole = (o) => {
  const { x, y, r = 12, id = "hole", tag = "hole", dwell = 0 } = o;
  return [
    SensorCircle(x, y, r, { tag, dwell, id }),
  ];
};

// ── WHIRLPOOL — the funnel ───────────────────────────────────
/** A conical pit. Gravity in the zone points at the CENTRE, so a
 *  ball that wanders in spirals inward, faster and faster, and is
 *  swallowed. Then the table re-serves it somewhere fun.
 *
 *  Returns { colliders, field } — because a whirlpool is the ONE
 *  device that needs a field as well as geometry, and hiding that
 *  from the table author would mean the field silently never gets
 *  mounted. (`table.fields` is a separate array the engine reads.
 *  A device that returns only colliders can never populate it.)
 *
 *  ══════════════════════════════════════════════════════════════
 *   ⚠ THE FIRST VERSION OF THIS DEVICE WAS A CAGE.
 *  ══════════════════════════════════════════════════════════════
 *
 *  It shipped a full 360° rim with `inner: true` — a SEALED RING
 *  the ball rides the inside of — and an `eye` sensor of r=11 at
 *  the centre, 20 units away across open air.
 *
 *  The grid drop found it instantly:
 *
 *      trapped: 58 / 304   (19.1% of the playfield)
 *      offenders: { "pool_rim": 58 }
 *
 *  Every single trapped ball was in the whirlpool. It went over the
 *  lip, the ring closed behind it, and then it bounced around the
 *  inside of a sealed circle FOREVER — because a ball on a curved
 *  wall with restitution 0.25 and a weak central pull will orbit
 *  the rim indefinitely without ever crossing the middle. The eye
 *  was a target it could MISS.
 *
 *  ── AND NO LAW COULD SEE IT ──────────────────────────────────
 *  The wedge law compares PAIRS of faces — the rim is ONE collider,
 *  so there was no pair. The ledge law checks SEGMENTS for pitch —
 *  the rim is an ARC, so it was never examined. The corridor law
 *  guards the plunger lane, which is nowhere near it.
 *
 *  Three laws, three blindnesses, and a device that eats one ball
 *  in five slid straight between them. This is the FOURTH failure
 *  mode the codebase said it would find, and it is a new SHAPE of
 *  failure, not a new instance of an old one:
 *
 *      A SEALED CONCAVE REGION WITH A MISSABLE EXIT.
 *
 *  Not a gap between two things. Not a flat shelf. A room with a
 *  door the ball can fail to find.
 *
 *  ── THE FIX: THE EXIT CANNOT BE MISSABLE ─────────────────────
 *  A catcher is allowed to catch — that is its job, and it is the
 *  one exemption the wedge law grants. But the exemption is
 *  conditional on the catcher HAVING AN EJECTOR, and an ejector
 *  the ball can orbit past is not an ejector.
 *
 *  So the eye is no longer a dot in the middle. It is sized to
 *  FILL the bowl: any ball inside the rim is inside the eye. The
 *  moment it crosses the lip it is caught, full stop. There is no
 *  trajectory that survives, no angle that orbits, no unlucky
 *  bounce that lives.
 *
 *  A pit you can fall into and climb back out of by accident is
 *  not a pit. It's a bug with a rim. */
export const whirlpool = (o) => {
  const {
    x, y, r = 46, id = "pool", tag = "pool", strength = 620,
    ballR = 7,
  } = o;

  // The mouth — a gap in the rim, so the ball has a way IN that
  // isn't "tunnel through a wall." (It could always get in by
  // clearing the lip, but a real funnel has a lead-in, and a lead-in
  // is also what makes the device visible as a THING you can aim at
  // rather than a hazard that ambushes you.)
  const mouth = o.mouth ?? -Math.PI / 2;   // faces UP by default
  const gapA  = o.gap ?? 0.85;

  return {
    colliders: [
      // The rim. NOT a sealed ring any more — it has a mouth. The
      // ball enters through the gap, and once inside, the eye takes
      // it immediately (see below), so the rim never gets a chance
      // to hold it.
      Arc(x, y, r, mouth + gapA / 2, mouth - gapA / 2 + TAU, {
        tag: "wall", inner: true, thickness: 5,
        restitution: 0.2, friction: 0.06, id: `${id}_rim`,
      }),
      // ⚠ THE EYE FILLS THE BOWL. It is not a target in the middle
      //   of the pit — IT IS THE PIT. Any ball that crosses the lip
      //   is inside it, on the very next physics step, at any angle,
      //   at any speed. That is the ENTIRE difference between this
      //   device and the cage it used to be. An exit the ball can
      //   miss is not an exit.
      SensorCircle(x, y, r - ballR - 1, { tag, id: `${id}_eye` }),
    ],
    field: MagnetFieldAt(x, y, { radius: r + 10, strength, id: `${id}_pull` }),
  };
};

// ── CAPTIVE BALL — the one you punch ─────────────────────────
/** A ball in a cage. You can't collect it; you HIT it, and it
 *  rattles, and the rattle scores. Cheap, tactile, and the oldest
 *  trick on a real machine.
 *
 *  Modelled as a fat post in a slot, because a genuinely free
 *  second ball would need ball-ball collision — which this solver
 *  does not do (see World: balls collide with GEOMETRY, never each
 *  other). So the captive is geometry that BEHAVES like a ball:
 *  a round, bouncy, high-restitution disc in a socket, which is
 *  what a captive ball feels like from the flipper anyway. */
export const captive = (o) => {
  const { x, y, id = "cap", tag = "captive", r = 9 } = o;
  return [
    Circle(x, y, r, {
      tag, restitution: 0.94, friction: 0.0, kick: 60,
      id, // ← the punchable one
    }),
  ];
};

// ── GATE — a real one-way flap ───────────────────────────────
/** Solid from one side, air from the other. The kit had this
 *  buried inside shell() as `lane_gate` and nowhere else, so no
 *  table could ever build a one-way of its own. It's a first-class
 *  device now.
 *
 *  `face` is the OUTWARD normal of the solid side: the direction a
 *  ball must be coming FROM to be stopped.
 *
 *  It KICKS by default, and that is not decoration — see the
 *  four-attempt saga in shell(). A passive flap in a tight lane is
 *  a shelf, and a shelf is a dead ball. Nothing passive goes in a
 *  box that tight. */
export const gate = (o) => {
  const { ax, ay, bx, by, id = "gate", face, kick = 120, tag = "wall" } = o;
  return [
    Seg(ax, ay, bx, by, {
      tag, thickness: 5, restitution: 0.3,
      oneSided: true, face, kick, id,
    }),
  ];
};

// ── TURNSTILE — the spinning cross ───────────────────────────
/** Four arms on a hub, rotating. The ball is batted by whichever
 *  arm arrives. Pure chaos, pure carnival.
 *
 *  Returns colliders AND a `spin(dt)` function. The table calls
 *  `spin` from its onTick, which rewrites the arms' endpoints.
 *  That's exactly how a Flipper works — a seg whose a/b move — so
 *  it costs the solver nothing.
 *
 *  ⚠ NO pointVel. A Flipper imparts its swing velocity to the ball
 *    (that's the `pointVel` term, and it's the entire feel of
 *    pinball). A turnstile deliberately does NOT: it would need a
 *    per-arm angular velocity threaded through, and an arm that
 *    both bats AND kicks flings the ball at Mach 3 into the glass.
 *    The turnstile gets a flat `kick` instead — bounded, tunable,
 *    and it cannot outrun the solver. Chaos, not catastrophe. */
export const turnstile = (o) => {
  const {
    x, y, id = "turn", arms = 4, len: L = 26,
    rate = 2.1, tag = "turnstile", kick = 190,
  } = o;

  const segs = [];
  for (let i = 0; i < arms; i++) {
    segs.push(Seg(x, y, x + L, y, {
      tag, thickness: 6, restitution: 0.6, kick,
      kickJitter: 0.2, id: `${id}_arm${i}`,
      // An arm sweeps through every angle including level. It is a
      // KICKER, so the ledge law exempts it automatically — a ball
      // cannot rest on a surface that refuses to hold still. That
      // exemption is not a loophole here; it is literally true.
    }));
  }
  // The hub, so a ball can't sit on the pivot.
  segs.push(Circle(x, y, 6, { tag: "post", id: `${id}_hub`, restitution: 0.5 }));

  let ang = 0;
  const spin = (dt) => {
    ang += rate * dt;
    for (let i = 0; i < arms; i++) {
      const a = ang + (i * TAU) / arms;
      const s = segs[i];
      s.b.x = x + Math.cos(a) * L;
      s.b.y = y + Math.sin(a) * L;
    }
  };
  spin(0);
  return { colliders: segs, spin };
};

// ── CART — a moving platform ─────────────────────────────────
/** A seg that slides back and forth along a track. The ball can
 *  ride it, be carried by it, and be dumped off the end.
 *
 *  Like turnstile, it returns a `move(dt)` for the table's onTick.
 *  Unlike turnstile, it CAN legitimately carry a ball, so it needs
 *  a pointVel — otherwise the ball sits on a platform sliding out
 *  from under it and simply falls through the world's idea of
 *  friction. A cart with no pointVel is a conveyor with no belt.
 *
 *  ══════════════════════════════════════════════════════════════
 *   ⚠ A MOVING SHELF IS STILL A SHELF.
 *  ══════════════════════════════════════════════════════════════
 *
 *  The first cart shipped with `pitch: 0.34` (19°) — comfortably
 *  past MIN_PITCH, so the LEDGE LAW PASSED IT WITHOUT COMMENT. And
 *  the grid drop found ELEVEN balls parked on it.
 *
 *      no CART  ->  at_rest 2
 *      ALL      ->  at_rest 11
 *
 *  Nine dead balls, on one collider, that three laws all approved.
 *
 *  ── WHY THE LEDGE LAW WAS BLIND ─────────────────────────────
 *  The ledge law asks "is this surface pitched enough that gravity
 *  will roll a ball off it?" — a question about ONE SURFACE, ALONE,
 *  IN A VACUUM. The cart is pitched. Gravity does roll the ball
 *  along it. The law is not wrong.
 *
 *  It just isn't the whole system. The ball rolls down the cart,
 *  reaches the low end... and the cart is STILL MOVING, and it
 *  carries the ball right back. The ball rolls down again. The cart
 *  carries it back again. `pointVel` — the very term that makes a
 *  cart a RIDE instead of a wall — is also the term that keeps
 *  feeding the ball back uphill, forever.
 *
 *      THE THING THAT MADE IT A RIDE IS THE THING THAT MADE IT A
 *      TRAP. The feature and the bug were the same line of code.
 *
 *  ── THE FIX: A CART MUST DUMP ───────────────────────────────
 *  A real mine cart tips. So this one does: at each end of its
 *  track it FIRES — a `kick` along its own face, ejecting whatever
 *  it's carrying into open air. It is a ride WITH AN EXIT, and an
 *  exit is the price of admission for anything on this table that
 *  can hold a ball at all.
 *
 *  This is the same law the scoop and the whirlpool live under,
 *  arriving from a completely different direction:
 *
 *      ANYTHING THAT CAN HOLD A BALL MUST SHIP WITH THE THING THAT
 *      LETS GO OF IT.
 *
 *  A scoop without an ejector is a grave. A whirlpool without a
 *  reachable eye is a cage. And a cart without a dump is a shelf on
 *  wheels — which is worse than a shelf, because it looks alive
 *  while it does it. */
export const cart = (o) => {
  const {
    ax, ay, bx, by,          // the TRACK the cart slides along
    len: L = 40,             // the cart's own width
    rate = 0.45,             // trips per second along the track
    id = "cart", tag = "cart", phase = 0,
    dump = 210,              // ← the kick at each end of the line
  } = o;

  const pitch = o.pitch ?? 0.34;      // ~19°
  let t = phase;
  let dir = 1;
  let vel = { x: 0, y: 0 };

  const body = Seg(0, 0, 0, 0, {
    tag, thickness: 7, restitution: 0.3, friction: 0.28, id,
    // ⚠ THE KICK IS NOT FLAVOUR — IT IS THE EXIT. See the header.
    //   Without it, `pointVel` carries the ball back uphill as fast
    //   as gravity rolls it down, and the ball rides forever. With
    //   it, the cart cannot hold a ball still, which is also exactly
    //   what earns it the ledge law's kicker exemption — honestly,
    //   this time, rather than by a pitch angle that was never the
    //   real question.
    kick: dump,
    kickJitter: 0.16,
    // The belt. Without this, the cart slides out from under the
    // ball. With it, the ball is CARRIED — which is the whole point
    // of a cart and the difference between a ride and a wall.
    pointVel: () => vel,
  });

  const move = (dt) => {
    t += dir * rate * dt;
    if (t >= 1) { t = 1; dir = -1; }
    if (t <= 0) { t = 0; dir =  1; }
    const px = ax + (bx - ax) * t;
    const py = ay + (by - ay) * t;
    const dx = Math.cos(pitch) * (L / 2);
    const dy = Math.sin(pitch) * (L / 2);
    body.a = { x: px - dx, y: py - dy };
    body.b = { x: px + dx, y: py + dy };
    // Its velocity along the track — handed to the solver so the
    // ball inherits the ride.
    const sp = rate * dir;
    vel = { x: (bx - ax) * sp, y: (by - ay) * sp };
  };
  move(0);
  return { colliders: [body], move };
};

// ── CHUTE — the slide ────────────────────────────────────────
/** A one-way tube: enter at the mouth, get flung out the far end.
 *  Physically it's just an entry sensor and an exit point — the
 *  ball never actually traverses it, it TELEPORTS. That's not a
 *  cheat, it's how a real under-playfield tunnel works: the ball
 *  vanishes at one hole and reappears at another, and nobody has
 *  ever felt cheated by it.
 *
 *  Returns the sensor; the table wires the warp in onSensor with
 *  `warpBall(e.ball, ...)`. */
export const chute = (o) => {
  const { x, y, r = 12, id = "chute", tag = "chute" } = o;
  return [SensorCircle(x, y, r, { tag, id })];
};

// ── FORK — the wishbone ──────────────────────────────────────
/** One mouth, a splitter post, two exits. Which way you go is
 *  decided by geometry and a hair of luck, and you cannot aim it.
 *  The carnival's favourite shape: identical inputs, different
 *  prizes.
 *
 *  The splitter is a POST, which means the wedge law is watching
 *  it — a post in a channel is exactly the `post_1 × ramp_L_railB`
 *  trap that the law was written to catch. So the throat is sized
 *  from the ball, not from taste: each branch gets a full
 *  ball-width of air, measured, or the build throws. */
export const fork = (o) => {
  const {
    x, y, id = "fork", spread = 46, depth = 58,
    tag = "fork", ballR = 7,
  } = o;
  const need = 2 * ballR + WEDGE_MARGIN;
  // Each branch is `need` of air. The two outer walls sit that far
  // out from the splitter, plus the splitter's own radius, plus
  // half a wall.
  const sp = Math.max(spread, need + 16);
  const pr = 8;   // splitter radius

  return [
    // The splitter — the coin-flip made of steel.
    Circle(x, y + 12, pr, {
      tag: "post", restitution: 0.85, id: `${id}_split`,
    }),
    // Left branch outer wall.
    Seg(x - pr - need - 3, y + 4, x - sp, y + depth, {
      tag: "wall", thickness: 5, restitution: 0.4, id: `${id}_L`,
    }),
    // Right branch outer wall.
    Seg(x + pr + need + 3, y + 4, x + sp, y + depth, {
      tag: "wall", thickness: 5, restitution: 0.4, id: `${id}_R`,
    }),
    // Which way did it go? Two sensors, and the table cares.
    SensorSeg(x - sp + 2, y + depth, x - pr - 2, y + depth, {
      tag: `${tag}_L`, thickness: 9, id: `${id}_outL`,
    }),
    SensorSeg(x + pr + 2, y + depth, x + sp - 2, y + depth, {
      tag: `${tag}_R`, thickness: 9, id: `${id}_outR`,
    }),
  ];
};

// ── WHEEL — the prize wheel ──────────────────────────────────
/** A ring of sensor wedges around a hub. The ball is dropped into
 *  it, rattles, and settles into ONE wedge — and that wedge is
 *  your prize. The purest carnival object there is: you did not
 *  earn this, you SPUN for it.
 *
 *  Each wedge is a sensor disc on the rim, tagged `${tag}_${i}`,
 *  so the table can hand out a different reward per slot. */
export const wheel = (o) => {
  const {
    x, y, r = 40, slots = 6, id = "wheel", tag = "prize",
  } = o;
  const out = [];
  // The hub — the ball can't rest on the centre.
  out.push(Circle(x, y, 9, {
    tag: "post", restitution: 0.9, kick: 70, id: `${id}_hub`,
  }));
  for (let i = 0; i < slots; i++) {
    const a = (i / slots) * TAU - Math.PI / 2;
    out.push(SensorCircle(
      x + Math.cos(a) * (r * 0.72),
      y + Math.sin(a) * (r * 0.72),
      9,
      { tag: `${tag}_${i}`, dwell: 0.1, id: `${id}_s${i}` }
    ));
  }
  return out;
};

// ── A magnet field, exported so whirlpool() can build one ────
// (World exports MagnetField; the kit re-wraps it so a table
//  never has to import from the physics layer directly. The
//  TABLE CONTRACT says a table imports from TableKit and nothing
//  else — the moment a table reaches into physics/, the layering
//  is decoration rather than architecture.)
export const MagnetFieldAt = (x, y, o = {}) => {
  const R = o.radius ?? 60;
  const strength = o.strength ?? 600;
  const c = { x, y };
  return {
    id: o.id ?? `mag_${x}_${y}`,
    kind: "magnet",
    c, radius: R,
    contains: (p) => {
      const dx = p.x - c.x, dy = p.y - c.y;
      return dx * dx + dy * dy < R * R;
    },
    accel: (p) => {
      const dx = c.x - p.x, dy = c.y - p.y;
      const d = Math.sqrt(dx * dx + dy * dy) || 1;
      const f = Math.max(0, Math.min(1, 1 - d / R));
      const s = strength * f * f;
      return { x: (dx / d) * s, y: (dy / d) * s };
    },
  };
};

export { Seg, Circle, Arc, Bumper, SensorSeg, SensorCircle, GravityZone };
